<template>
  <div v-if="show" class="fixed inset-0 z-50 flex flex-col bg-slate-50 overflow-hidden font-sans">

    <div class="absolute top-[-20%] left-[-10%] w-[800px] h-[800px] bg-indigo-400/20 rounded-full blur-[120px] pointer-events-none animate-pulse-slow"></div>
    <div class="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] bg-purple-400/20 rounded-full blur-[100px] pointer-events-none animate-pulse-slow delay-700"></div>

    <!-- Barra de progreso superior -->
    <div v-if="currentStep < 6" class="relative z-10 w-full bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4 flex items-center justify-between shadow-sm">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
          <Wand2 class="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 class="text-sm font-bold text-slate-900">{{ form.id ? 'Editando Agente' : 'Configurador de Agente' }}</h2>
          <p class="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">Paso {{ currentStep }} de 5</p>
        </div>
      </div>
      <button @click="closeWizard" class="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors">
        <X class="w-5 h-5" />
      </button>
    </div>

    <!-- Contenedor Principal con Transiciones -->
    <div class="flex-1 relative z-10 flex items-center justify-center p-4 sm:p-8 overflow-y-auto custom-scrollbar">
      <transition name="slide-fade" mode="out-in">

        <!-- PASO 1: IDENTIDAD Y SELECTOR CON BÚSQUEDA DINÁMICO -->
        <div v-if="currentStep === 1" class="w-full max-w-2xl bg-white border border-slate-200/80 rounded-3xl p-8 shadow-2xl shadow-slate-200/50" key="step1">
          <div class="text-center mb-6">
            <h2 class="text-3xl font-black text-slate-900 tracking-tight">Personaliza tu <span class="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-600">Agente</span></h2>
            <p class="text-slate-500 mt-1 text-sm">Selecciona el tipo de negocio para cargar sus reglas de atención correspondientes.</p>
          </div>

          <div class="space-y-5">

            <!-- BUSCADOR / COMBOBOX DE GIRO DE NEGOCIO (FETCHED DESDE BD) -->
            <div class="relative" ref="verticalDropdownRef">
              <label class="text-xs font-bold text-slate-600 uppercase mb-1.5 block">Giro o Rubro de la Empresa</label>

              <!-- Botón disparador del selector -->
              <div
                @click="isDropdownOpen = !isDropdownOpen"
                class="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm flex items-center justify-between cursor-pointer hover:bg-white hover:border-indigo-400 transition-all shadow-sm"
                :class="{ 'ring-2 ring-indigo-500/20 border-indigo-500 bg-white': isDropdownOpen }"
              >
                <div class="flex items-center gap-3 truncate">
                  <div class="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                    <component :is="getVerticalIcon(selectedVertical.slug)" class="w-4 h-4" />
                  </div>
                  <div class="truncate text-left">
                    <span class="font-bold text-slate-800 block truncate leading-tight">
                      {{ selectedVertical.name || (isLoadingVerticals ? 'Cargando rubros...' : 'Selecciona un rubro...') }}
                    </span>
                    <span class="text-[10px] text-slate-400 block truncate">
                      {{ selectedVertical.default_role ? `Rol sugerido: ${selectedVertical.default_role}` : 'Escribe para buscar entre las opciones' }}
                    </span>
                  </div>
                </div>
                <div class="flex items-center gap-1 shrink-0">
                  <Loader2 v-if="isLoadingVerticals" class="w-4 h-4 text-indigo-600 animate-spin mr-1" />
                  <ChevronDown class="w-4 h-4 text-slate-400 transition-transform duration-200" :class="{ 'rotate-180': isDropdownOpen }" />
                </div>
              </div>

              <!-- Menú Desplegable con Input de Búsqueda -->
              <div
                v-if="isDropdownOpen"
                class="absolute left-0 right-0 top-full mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl shadow-slate-300/50 z-50 overflow-hidden animate-fade-in"
              >
                <!-- Input de filtro -->
                <div class="p-3 border-b border-slate-100 bg-slate-50/50">
                  <div class="relative">
                    <Search class="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      ref="searchInputRef"
                      v-model="searchQuery"
                      type="text"
                      class="w-full pl-9 pr-8 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
                      placeholder="Escribe para buscar (Ej: Software, Clínica, Abogados, Delivery, Taller...)"
                    />
                    <button v-if="searchQuery" @click="searchQuery = ''" class="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      <X class="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <!-- Lista de opciones filtradas -->
                <div class="max-h-56 overflow-y-auto custom-scrollbar p-1.5 space-y-1">
                  <div
                    v-for="v in filteredVerticals"
                    :key="v.slug"
                    @click="chooseVertical(v)"
                    class="p-2.5 rounded-xl flex items-center justify-between cursor-pointer transition-all hover:bg-indigo-50/80 group"
                    :class="form.business_type === v.slug ? 'bg-indigo-50 text-indigo-950 font-bold' : 'text-slate-700'"
                  >
                    <div class="flex items-center gap-3">
                      <component :is="getVerticalIcon(v.slug)" class="w-4 h-4 text-slate-400 group-hover:text-indigo-600" :class="{ '!text-indigo-600': form.business_type === v.slug }" />
                      <div>
                        <p class="text-xs font-semibold leading-tight">{{ v.name }}</p>
                        <p class="text-[10px] text-slate-400 leading-tight mt-0.5">Rol: {{ v.default_role }}</p>
                      </div>
                    </div>
                    <Check v-if="form.business_type === v.slug" class="w-4 h-4 text-indigo-600" />
                  </div>

                  <div v-if="filteredVerticals.length === 0" class="p-4 text-center text-xs text-slate-400">
                    No se encontraron rubros con "<span class="text-slate-600 font-semibold">{{ searchQuery }}</span>".
                  </div>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="text-xs font-bold text-slate-600 uppercase mb-1.5 block">Nombre del Asistente</label>
                <input v-model="form.agent_name" type="text" :class="inputClasses" placeholder="Ej: Sofía, Carlos, Asesor Virtual" />
              </div>
              <div>
                <label class="text-xs font-bold text-slate-600 uppercase mb-1.5 block">Cargo o Rol</label>
                <input v-model="form.agent_role" type="text" :class="inputClasses" placeholder="Ej: Asesor de Proyectos, Recepción" />
              </div>
            </div>

            <div>
              <label class="text-xs font-bold text-slate-600 uppercase mb-1.5 block">Nombre de tu Empresa</label>
              <input v-model="form.company_name" type="text" :class="inputClasses" placeholder="Ej: Devs Studio, Clínica Dental Pro..." />
            </div>
          </div>

          <div class="mt-8 flex justify-end">
            <button @click="nextStep" :disabled="!form.agent_name || !form.company_name" :class="btnPrimary">
              Siguiente <ArrowRight class="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>

        <!-- PASO 2: CATÁLOGO -->
        <div v-else-if="currentStep === 2" class="w-full max-w-3xl bg-white border border-slate-200/80 rounded-3xl p-8 shadow-2xl shadow-slate-200/50" key="step2">
          <div class="text-center mb-6">
            <div class="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <ShoppingBag class="w-6 h-6" />
            </div>
            <h2 class="text-2xl font-black text-slate-900 tracking-tight">Catálogo de Productos y Servicios</h2>
            <p class="text-slate-500 mt-1 text-sm">Define precios e información que el asistente utilizará para responder.</p>
          </div>

          <div class="space-y-4 max-h-[400px] overflow-y-auto custom-scrollbar pr-2">
            <transition-group name="list">
              <div v-for="(item, index) in form.catalog" :key="index" class="p-4 bg-slate-50 border border-slate-200 rounded-2xl flex gap-4">

                <label class="w-20 h-20 shrink-0 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-all overflow-hidden relative group bg-white">
                  <input type="file" class="hidden" accept="image/*" @change="e => uploadProductImage(e, item)" />
                  <template v-if="item.isUploading">
                    <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                  </template>
                  <template v-else-if="item.image_url">
                    <img :src="item.image_url" class="w-full h-full object-cover" />
                    <div class="absolute inset-0 bg-slate-900/40 hidden group-hover:flex items-center justify-center">
                      <Camera class="w-5 h-5 text-white" />
                    </div>
                  </template>
                  <template v-else>
                    <Camera class="w-6 h-6 text-slate-400 mb-1 group-hover:text-indigo-500 transition-colors" />
                    <span class="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Foto</span>
                  </template>
                </label>

                <div class="flex-1 flex flex-col gap-2">
                  <div class="flex items-center justify-between gap-2">
                    <div class="flex-1 flex gap-2">
                      <input v-model="item.name" type="text" :class="inputClasses" placeholder="Nombre (Ej: Desarrollo Web / Envíos)" />
                      <input v-model.number="item.price" type="number" step="0.01" class="w-28 px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-800" placeholder="0 = Cotizar" />
                      <select v-model="item.type" class="px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600">
                        <option value="Service">Servicio</option>
                        <option value="Product">Producto</option>
                      </select>
                    </div>
                    <button v-if="form.catalog.length > 1" @click="removeItem('catalog', index)" class="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors shrink-0">
                      <Trash2 class="w-4 h-4" />
                    </button>
                  </div>
                  <input v-model="item.description" type="text" class="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none" placeholder="Descripción breve o características..." />
                </div>
              </div>
            </transition-group>
          </div>

          <div class="mt-4">
            <button v-if="form.catalog.length < maxProducts" @click="addItem('catalog')" class="flex items-center gap-2 text-indigo-600 font-bold text-sm hover:text-indigo-800 transition-colors bg-indigo-50 px-4 py-2 rounded-xl">
              <PlusCircle class="w-4 h-4" /> Añadir otro ítem
            </button>
            <p v-else class="text-xs font-bold text-orange-500 flex items-center gap-1">
              <ShieldAlert class="w-4 h-4" /> Límite alcanzado ({{ maxProducts }}).
            </p>
          </div>

          <div class="mt-8 flex justify-between">
            <button @click="prevStep" :class="btnGhost"><ArrowLeft class="w-4 h-4 mr-2" /> Atrás</button>
            <button @click="nextStep" :class="btnPrimary">Siguiente <ArrowRight class="w-4 h-4 ml-2" /></button>
          </div>
        </div>

        <!-- PASO 3: POLÍTICAS -->
        <div v-else-if="currentStep === 3" class="w-full max-w-2xl bg-white border border-slate-200/80 rounded-3xl p-8 shadow-2xl shadow-slate-200/50" key="step3">
          <div class="text-center mb-6">
            <div class="w-12 h-12 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Store class="w-6 h-6" />
            </div>
            <h2 class="text-2xl font-black text-slate-900 tracking-tight">Políticas del Negocio</h2>
            <p class="text-slate-500 mt-1 text-sm">Condiciones comerciales fijas (Horarios, Métodos de Pago, Tiempos de Entrega).</p>
          </div>

          <div class="space-y-3 max-h-60 overflow-y-auto custom-scrollbar pr-2">
            <transition-group name="list">
              <div v-for="(item, index) in form.policies" :key="'pol'+index" class="flex items-center gap-2">
                <div class="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-400">{{ index + 1 }}</div>
                <input v-model="form.policies[index]" type="text" :class="inputClasses" placeholder="Ej: Métodos de pago: Transferencia BAC y Efectivo" />
                <button v-if="form.policies.length > 1" @click="removeItem('policies', index)" class="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors">
                  <Trash2 class="w-5 h-5" />
                </button>
              </div>
            </transition-group>
          </div>

          <div class="mt-4">
            <button v-if="form.policies.length < maxRules" @click="addItem('policies')" class="flex items-center gap-2 text-purple-600 font-bold text-sm hover:text-purple-800 transition-colors">
              <PlusCircle class="w-5 h-5" /> Agregar regla operativa
            </button>
          </div>

          <div class="mt-8 flex justify-between">
            <button @click="prevStep" :class="btnGhost"><ArrowLeft class="w-4 h-4 mr-2" /> Atrás</button>
            <button @click="nextStep" :class="btnPrimary">Siguiente <ArrowRight class="w-4 h-4 ml-2" /></button>
          </div>
        </div>

        <!-- PASO 4: COMPORTAMIENTO -->
        <div v-else-if="currentStep === 4" class="w-full max-w-2xl bg-white border border-slate-200/80 rounded-3xl p-8 shadow-2xl shadow-slate-200/50" key="step4">
          <div class="text-center mb-6">
            <div class="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <BrainCircuit class="w-6 h-6" />
            </div>
            <h2 class="text-2xl font-black text-slate-900 tracking-tight">Directivas de Atención</h2>
            <p class="text-slate-500 mt-1 text-sm">Instrucciones de tono y pautas específicas de tu equipo comercial.</p>
          </div>

          <div class="space-y-3 max-h-60 overflow-y-auto custom-scrollbar pr-2 mb-4">
            <transition-group name="list">
              <div v-for="(item, index) in form.sales_rules" :key="'rule'+index" class="flex items-center gap-2">
                <div class="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-400">{{ index + 1 }}</div>
                <input v-model="form.sales_rules[index]" type="text" :class="inputClasses" placeholder="Ej: Invita al cliente a una llamada breve de asesoría" />
                <button v-if="form.sales_rules.length > 1" @click="removeItem('sales_rules', index)" class="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors">
                  <Trash2 class="w-5 h-5" />
                </button>
              </div>
            </transition-group>
          </div>

          <div class="mt-4">
            <button v-if="form.sales_rules.length < maxRules" @click="addItem('sales_rules')" class="flex items-center gap-2 text-emerald-600 font-bold text-sm hover:text-emerald-800 transition-colors mb-6">
              <PlusCircle class="w-5 h-5" /> Agregar instrucción de venta
            </button>
          </div>

          <div class="mt-8 flex justify-between">
            <button @click="prevStep" :class="btnGhost"><ArrowLeft class="w-4 h-4 mr-2" /> Atrás</button>
            <button @click="nextStep" :class="btnPrimary">Siguiente <ArrowRight class="w-4 h-4 ml-2" /></button>
          </div>
        </div>

        <!-- PASO 5: INSTANCIA -->
        <div v-else-if="currentStep === 5" class="w-full max-w-3xl bg-white border border-slate-200/80 rounded-3xl p-8 shadow-2xl shadow-slate-200/50" key="step5">
          <div class="text-center mb-6">
            <div class="w-12 h-12 bg-sky-50 text-sky-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <MessageCircle class="w-6 h-6" />
            </div>
            <h2 class="text-2xl font-black text-slate-900 tracking-tight">Conecta a {{ form.agent_name || 'tu Asistente' }}</h2>
            <p class="text-slate-500 mt-1 text-sm">Selecciona la línea de WhatsApp para desplegar la atención automatizada.</p>
          </div>

          <div v-if="processedInstances.length === 0" class="text-center p-8 border border-dashed border-slate-300 rounded-2xl bg-slate-50 mb-8">
            <Smartphone class="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p class="text-slate-600 font-bold text-sm">No tienes conexiones disponibles.</p>
            <p class="text-xs text-slate-400 mt-1">Crea una instancia en la sección de Conexiones.</p>
          </div>

          <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
            <div
              v-for="inst in processedInstances"
              :key="inst.name"
              @click="!inst.inUse ? form.instance_name = inst.name : null"
              class="relative border-2 rounded-2xl p-4 transition-all"
              :class="[
                inst.inUse ? 'border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-sky-300',
                form.instance_name === inst.name ? 'border-sky-500 bg-sky-50 shadow-md shadow-sky-500/20' : (!inst.inUse ? 'border-slate-200 bg-white' : '')
              ]"
            >
              <div v-if="form.instance_name === inst.name" class="absolute top-3 right-3 text-sky-500">
                <CheckCircle2 class="w-5 h-5 fill-current bg-white rounded-full" />
              </div>

              <div class="flex items-center gap-3">
                <img v-if="inst.profilePicUrl" :src="inst.profilePicUrl" class="w-10 h-10 rounded-full object-cover border border-slate-200" />
                <div v-else class="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                  <Smartphone class="w-5 h-5" />
                </div>
                <div>
                  <h4 class="font-bold text-slate-900 text-sm truncate w-40" :class="{ 'text-slate-500': inst.inUse }">{{ inst.name }}</h4>
                  <p class="text-[10px] font-mono" :class="inst.connectionStatus === 'open' ? 'text-emerald-600' : 'text-slate-400'">
                    {{ inst.ownerJid ? '+' + inst.ownerJid.split('@')[0] : (inst.connectionStatus === 'open' ? 'Conectado' : 'Desconectado') }}
                  </p>
                </div>
              </div>
            </div>

            <div
              @click="form.instance_name = ''"
              class="relative border-2 rounded-2xl p-4 transition-all cursor-pointer hover:border-slate-300"
              :class="form.instance_name === '' ? 'border-slate-800 bg-slate-900 text-white shadow-md' : 'border-slate-200 bg-white text-slate-700'"
            >
              <div v-if="form.instance_name === ''" class="absolute top-3 right-3 text-white">
                <CheckCircle2 class="w-5 h-5 fill-current bg-slate-900 rounded-full" />
              </div>
              <div class="flex items-center gap-3 h-full">
                <div class="w-10 h-10 rounded-full flex items-center justify-center" :class="form.instance_name === '' ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-400'">
                  <PowerOff class="w-5 h-5" />
                </div>
                <div>
                  <h4 class="font-bold text-sm">Guardar sin conectar</h4>
                  <p class="text-[10px]" :class="form.instance_name === '' ? 'text-slate-400' : 'text-slate-500'">Configurar cerebro únicamente</p>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-8 flex justify-between pt-6 border-t border-slate-100">
            <button @click="prevStep" :class="btnGhost"><ArrowLeft class="w-4 h-4 mr-2" /> Atrás</button>
            <button @click="generateMagicAgent" :class="[btnPrimary, 'shadow-emerald-500/30 !from-emerald-500 !to-teal-600']">
              <Sparkles class="w-4 h-4 mr-2" /> {{ form.id ? 'Guardar Cambios' : 'Desplegar Agente' }}
            </button>
          </div>
        </div>

        <!-- PASO 6: PANTALLA DE CARGA -->
        <div v-else-if="currentStep === 6" class="w-full flex flex-col items-center justify-center" key="step6">
          <div class="relative w-32 h-32 mb-8 flex items-center justify-center">
            <div class="absolute inset-0 border-4 border-t-indigo-500 border-r-transparent border-b-purple-500 border-l-transparent rounded-full animate-spin-slow"></div>
            <div class="absolute inset-2 border-4 border-t-transparent border-r-emerald-500 border-b-transparent border-l-indigo-400 rounded-full animate-spin-reverse-slow"></div>
            <Brain class="w-10 h-10 text-slate-700 animate-pulse" />
          </div>

          <h2 class="text-2xl font-black text-slate-900 tracking-tight mb-2">
            {{ form.id ? 'Actualizando cerebro...' : 'Construyendo cerebro...' }}
          </h2>

          <transition name="fade" mode="out-in">
            <p :key="loadingTextIndex" class="text-indigo-600 font-medium text-sm h-6">
              {{ loadingTexts[loadingTextIndex] }}
            </p>
          </transition>

          <div class="w-64 h-2 bg-slate-200 rounded-full mt-6 overflow-hidden">
            <div class="h-full bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-[3000ms] ease-out" :style="`width: ${loadingProgress}%`"></div>
          </div>
        </div>

      </transition>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onUnmounted, onMounted, computed, watch, nextTick } from 'vue';
import {
  Wand2, X, ArrowRight, ArrowLeft, ShoppingBag, PlusCircle,
  Trash2, Store, BrainCircuit, Sparkles, Brain, MessageCircle,
  Smartphone, CheckCircle2, PowerOff, ShieldAlert, Camera,
  Code2, Truck, Stethoscope, Store as StoreIcon, Wrench,
  Search, ChevronDown, Check, Scale, Utensils, Home, GraduationCap,
  Car, Eye as EyeIcon, Sun, PartyPopper, Briefcase, Loader2
} from 'lucide-vue-next';
import { useUpload } from '@/composables/useUpload';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';
import { SecureStorage } from '@/utils/secureStorage';
import axios from 'axios';

const { uploadFile, deleteFile } = useUpload();

const BASE_URL = 'https://agentes.therealdevs.pro/index.php';
const EVO_URL = 'https://wapi.therealdevs.pro';
const EVO_KEY = 'TheRealDevsPro2026';
const inputClasses = "w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all placeholder:text-slate-400 flex-1 no-uppercase";
const btnPrimary = "flex items-center justify-center px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-bold rounded-xl shadow-lg shadow-indigo-500/30 transition-all active:scale-95 disabled:opacity-50 disabled:pointer-events-none cursor-pointer";
const btnGhost = "flex items-center justify-center px-6 py-3 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-bold rounded-xl transition-all active:scale-95 cursor-pointer";

const props = defineProps({
  show: Boolean,
  evolutionInstances: { type: Array, default: () => [] },
  existingAgents: { type: Array, default: () => [] },
  agentToEdit: { type: Object, default: null },
  planLimits: { type: Object, default: null }
});

const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError } = useToast();

const currentStep = ref(1);
const currentUser = SecureStorage.getItem('neurals_user');

const maxProducts = computed(() => props.planLimits?.max_products || 5);
const maxRules = computed(() => props.planLimits?.max_rules || 5);
const maxWordsResponse = computed(() => props.planLimits?.max_words_response || 45);

// ==========================================
// ESTADO DINÁMICO DE VERTICALES / RUBROS
// ==========================================
const verticals = ref([]);
const isLoadingVerticals = ref(false);

const isDropdownOpen = ref(false);
const searchQuery = ref('');
const searchInputRef = ref(null);
const verticalDropdownRef = ref(null);

// Mapeo dinámico de iconos según el slug de BD
const getVerticalIcon = (slug) => {
  const iconMap = {
    software_it: Code2,
    delivery_logistics: Truck,
    medical_clinic: Stethoscope,
    dental_clinic: Stethoscope,
    ecommerce_retail: StoreIcon,
    legal_accounting: Scale,
    restaurant_gastronomy: Utensils,
    real_estate: Home,
    education_academy: GraduationCap,
    automotive_dealership: Car,
    auto_repair: Wrench,
    beauty_salon_spa: Sparkles,
    fitness_gym: Sparkles,
    tourism_hospitality: Home,
    veterinary_petcare: Stethoscope,
    construction_architecture: Wrench,
    events_catering: PartyPopper,
    solar_energy_hvac: Sun,
    optometry_eyecare: EyeIcon,
    general_services: Briefcase
  };
  return iconMap[slug] || StoreIcon;
};

// Cargar verticales desde la Base de Datos
const fetchVerticals = async () => {
  if (verticals.value.length > 0) return;
  isLoadingVerticals.value = true;
  try {
    const res = await apiClient.get('/business_verticals');
    const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
    verticals.value = data;

    // Si ya cargaron y no hay seleccionado, asigna el primero
    if (verticals.value.length > 0 && !form.business_type) {
      chooseVertical(verticals.value[0]);
    }
  } catch (err) {
    console.error('Error al cargar business_verticals:', err);
  } finally {
    isLoadingVerticals.value = false;
  }
};

const filteredVerticals = computed(() => {
  if (!searchQuery.value.trim()) return verticals.value;
  const q = searchQuery.value.toLowerCase();
  return verticals.value.filter(v =>
    v.name?.toLowerCase().includes(q) ||
    v.default_role?.toLowerCase().includes(q)
  );
});

const selectedVertical = computed(() => {
  return verticals.value.find(v => v.slug === form.business_type) || {};
});

const chooseVertical = (v) => {
  form.business_type = v.slug;
  form.estimation_rules = v.estimation_rules || '';
  if (!form.agent_role || form.agent_role === 'Consultor Tecnológico') {
    form.agent_role = v.default_role;
  }

  // Parsear default_policies si vienen en JSON
  let defaultPolicies = [];
  try {
    defaultPolicies = typeof v.default_policies === 'string'
      ? JSON.parse(v.default_policies)
      : (v.default_policies || []);
  } catch (e) {
    defaultPolicies = [];
  }

  if (defaultPolicies.length > 0 && form.policies.length === 1 && form.policies[0] === '') {
    form.policies = [...defaultPolicies];
  }

  isDropdownOpen.value = false;
  searchQuery.value = '';
};

// Cerrar dropdown al hacer click afuera
const handleClickOutside = (e) => {
  if (verticalDropdownRef.value && !verticalDropdownRef.value.contains(e.target)) {
    isDropdownOpen.value = false;
  }
};

watch(isDropdownOpen, async (open) => {
  if (open) {
    await nextTick();
    searchInputRef.value?.focus();
  }
});

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const defaultProduct = () => ({ id: null, name: '', price: 0, type: 'Service', description: '', image_url: '', isUploading: false });

const form = reactive({
  id: null,
  agent_name: '',
  agent_role: '',
  company_name: '',
  business_type: '',
  estimation_rules: '',
  catalog: [defaultProduct()],
  policies: [''],
  sales_rules: [''],
  instance_name: '',
  owner: currentUser?.id || null
});

const loadingProgress = ref(0);
const loadingTextIndex = ref(0);
let textInterval = null;

const loadingTexts = [
  "Iniciando calibración sináptica...",
  "Inyectando reglas anti-bucle del giro de negocio...",
  "Guardando catálogo estructurado...",
  "Configurando directivas de embudo de ventas...",
  "Blindando agente contra alucinaciones de precios...",
  "Vinculando webhook de WhatsApp..."
];

watch(() => props.show, async (val) => {
  if (val) {
    currentStep.value = 1;
    loadingTextIndex.value = 0;
    isDropdownOpen.value = false;
    searchQuery.value = '';

    await fetchVerticals();

    if (props.agentToEdit) {
      form.id = props.agentToEdit.id;
      form.agent_name = props.agentToEdit.name || '';
      form.company_name = props.agentToEdit.bio ? props.agentToEdit.bio.replace('Asistente de ', '') : '';
      form.instance_name = props.agentToEdit.instance_name || '';
      form.business_type = props.agentToEdit.business_type || 'general_services';
      form.policies = [''];
      form.sales_rules = [''];

      try {
        const resProd = await apiClient.get('/products_services', {
          params: { ai_character: props.agentToEdit.id }
        });
        const items = Array.isArray(resProd.data) ? resProd.data : (resProd.data?.data || []);

        if (items.length > 0) {
          form.catalog = items.map(p => ({
            id: p.id,
            name: p.name || '',
            price: Number(p.price) || 0,
            type: p.type || 'Service',
            description: p.description || '',
            image_url: p.photo_url || p.image_url || '',
            isUploading: false
          }));
        } else {
          form.catalog = [defaultProduct()];
        }
      } catch (err) {
        console.error('Error al cargar catálogo:', err);
        form.catalog = [defaultProduct()];
      }
    } else {
      form.id = null;
      form.agent_name = '';
      form.company_name = '';
      form.agent_role = '';
      form.instance_name = '';
      form.catalog = [defaultProduct()];
      form.policies = [''];
      form.sales_rules = [''];

      if (verticals.value.length > 0) {
        chooseVertical(verticals.value[0]);
      }
    }
  }
}, { immediate: true });

const processedInstances = computed(() => {
  return props.evolutionInstances.map(inst => {
    const isInUse = props.existingAgents.some(
      agent => agent.instance_name === inst.name && agent.id !== form.id
    );
    return { ...inst, inUse: isInUse };
  });
});

const nextStep = () => { if (currentStep.value < 5) currentStep.value++; };
const prevStep = () => { if (currentStep.value > 1) currentStep.value--; };
const closeWizard = () => emit('close');

const addItem = (field) => {
  if (field === 'catalog') {
    form.catalog.push(defaultProduct());
  } else {
    form[field].push('');
  }
};

const removeItem = (field, index) => form[field].splice(index, 1);

const uploadProductImage = async (e, item) => {
  const file = e.target.files[0];
  if (!file) return;

  const oldPhoto = item.image_url;
  const url = await uploadFile(file);
  if (url) {
    if (oldPhoto) await deleteFile(oldPhoto);
    item.image_url = url;
    showSuccess('Foto adjuntada');
  }
};

const generateMagicAgent = async () => {
  currentStep.value = 6;
  loadingProgress.value = 0;
  let savedAgentId = form.id;
  setTimeout(() => { loadingProgress.value = 85; }, 100);

  textInterval = setInterval(() => {
    if (loadingTextIndex.value < loadingTexts.length - 1) {
      loadingTextIndex.value++;
    }
  }, 800);

  try {
    const validProducts = form.catalog.filter(p => p.name.trim() !== '');

    const cleanPayload = {
      ...form,
      is_online: 1,
      catalog: validProducts.map(p => `${p.name}: C$${p.price} (${p.description || p.type})`),
      policies: form.policies.filter(i => i.trim() !== ''),
      sales_rules: form.sales_rules.filter(i => i.trim() !== ''),
      max_words: maxWordsResponse.value
    };

    if (form.id) {
      await apiClient.put(`/ai_character_wizard/${form.id}`, cleanPayload);
    } else {
      const res = await apiClient.post('/ai_character_wizard', cleanPayload);
      savedAgentId = res.data?.id || res.data?.data?.id;
    }

    if (savedAgentId && validProducts.length > 0) {
      const productPromises = validProducts.map(prod => {
        const productPayload = {
          ai_character: savedAgentId,
          owner: currentUser?.id || 1,
          name: prod.name,
          price: prod.price || 0,
          type: prod.type || 'Service',
          description: prod.description || '',
          is_available: 1,
          photo_url: prod.image_url || ''
        };

        if (prod.id) {
          return apiClient.put(`/products_services/${prod.id}`, productPayload);
        } else {
          return apiClient.post('/products_services', productPayload);
        }
      });
      await Promise.all(productPromises);
    }

    if (form.instance_name && savedAgentId) {
      const webhookUrl = `${BASE_URL}/webhook_whatsapp/${savedAgentId}`;

      await axios.post(`${EVO_URL}/webhook/set/${form.instance_name}`, {
        webhook: {
          enabled: true,
          url: webhookUrl,
          webhookByEvents: true,
          base64: false,
          events: ["MESSAGES_UPSERT"]
        }
      }, { headers: { apikey: EVO_KEY } });

      await axios.post(`${EVO_URL}/settings/set/${form.instance_name}`, {
        rejectCall: false,
        msgCall: "En este momento no puedo contestar, por favor inténtelo luego.",
        groupsIgnore: true,
        alwaysOnline: true,
        readMessages: false,
        readStatus: false,
        syncFullHistory: false
      }, { headers: { apikey: EVO_KEY } });
    }

    setTimeout(() => {
      loadingProgress.value = 100;
      clearInterval(textInterval);

      setTimeout(() => {
        showSuccess(`¡Agente ${form.agent_name} calibrado y desplegado con éxito!`);
        emit('saved');
        emit('close');
      }, 500);
    }, 1200);

  } catch (error) {
    console.error(error);
    clearInterval(textInterval);
    showError('Error al guardar el agente.');
    currentStep.value = 5;
  }
};

onUnmounted(() => {
  if (textInterval) clearInterval(textInterval);
});
</script>

<style scoped>
.slide-fade-enter-active { transition: all 0.4s ease-out; }
.slide-fade-leave-active { transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1); }
.slide-fade-enter-from { transform: translateX(30px); opacity: 0; }
.slide-fade-leave-to { transform: translateX(-30px); opacity: 0; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.list-enter-active, .list-leave-active { transition: all 0.3s ease; }
.list-enter-from, .list-leave-to { opacity: 0; transform: translateY(-15px); }
.animate-spin-slow { animation: spin 4s linear infinite; }
.animate-spin-reverse-slow { animation: spin-reverse 3s linear infinite; }
@keyframes spin-reverse { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
.animate-pulse-slow { animation: pulse 6s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
</style>